<template>
  <Layout>
    <McLoading :isLoading="isTransitionLoading" />
    <PageHeader :title="title" :sub-title="subTitle">
      <template #afterTitle>
        <div class="d-flex gap-2">
          <McButton variant="primary" :text="$t('global.action.create')" icon="icon-outline_plus" @click="handleAdd" />
        </div>
      </template>
    </PageHeader>

    <McTable
      v-show="isTableDataAvailable"
      ref="table-sales-order"
      :key="tableKey"
      class="table-sales-order"
      withCard
      stickyHeader
      searchable
      hasCheckbox
      bulkDeleteApi
      hasEmit:deleteSuccess
      :fields="fields"
      :apiClient="apiClient"
      :activeFilters="activeFilters"
      :initialPage="initialParams.page"
      :initialLimit="initialParams.limit"
      :initialSearch="initialParams.search"
      :customHandlers="{
        handleClickRow,
        handleOpenNewTab,
      }"
      @deleteSuccess="handleDeleteSuccess"
      @available-change="handleDataAvailable"
      @handleSelectValue="handleClickCheckbox"
    >
      <template #before-table="{ isSelected, showDeleteModal }">
        <div class="d-flex justify-content-between mb-3">
          <McInlineFilter
            :filterItems="inlineFilterItems"
            :activeKeys="activeFilters['status.name']"
            @update:activeKeys="handleChangeActiveFilters([{ key: 'status.name', values: $event }])"
          />

          <div class="d-flex gap-2">
            <McTableFilter
              :activeFilters="activeFilters"
              :parseFilterDataMappings="parseFilterDataMappings"
              @submit="handleChangeActiveFilters"
            >
              <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
                <InputTableFilter
                  :formValues="formValues"
                  :fullObjects="fullObjects"
                  @update:formValues="updateFormValues($event)"
                  @update:fullObjects="updateFullObjects($event)"
                />
              </template>
            </McTableFilter>

            <ActionDropdown
              :disabled="!isSelected"
              :actionHandlers="{
                handleDelete: showDeleteModal,
                handleTransitionTo,
                handleTransitionToCanceled,
              }"
            />
          </div>
        </div>
      </template>

      <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataFields"
          @setColumn="handleSetDataFields"
        />
      </template>
      
      <!-- status.name -->
      <template #[`cell(status.name)`]="{ item }">
        <span
          v-if="item.status?.color"
          class="__status-dots"
          :style="{ backgroundColor: item.status.color }"
        ></span>
        <span class="mc-text-status">{{ computedStatus(item.status.raw_type) }}</span>
      </template>

      <!-- created_on -->
      <template #cell(created_on)="{ value }">
        <template v-if="value">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template v-else> - </template>
      </template>

      <!-- target.picked_on -->
      <template #[`cell(target.picked_on)`]="{ value }">
        <template v-if="value">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template v-else> - </template>
      </template>

      <!-- target.delivered_on -->
      <template #[`cell(target.delivered_on)`]="{ value }">
        <template v-if="value">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template v-else> - </template>
      </template>
    </McTable>

    <!-- Table Empty Data -->
    <b-card v-show="!isTableDataAvailable" class="mt-3">
      <div class="empty-table-container">
        <img :src="illustrationPath" alt />
        <strong>{{ $t('salesOrder.text.emptyData') }}</strong>
        <p>{{ $t('salesOrder.text.addNewData') }}</p>
        <McButton
          variant="primary"
          :text="$t('global.addField')"
          icon="icon-outline_plus"
          @click="handleAdd"
        />
      </div>
    </b-card>

    <!-- Transition to new Status -->
    <DefaultTransitionModal
      ref="modal-transition"
      :textSecondaryBtn="$t('global.cancel')"
      :textPrimaryBtn="$t('salesOrder.action.confirm')"
      :entityData="{
        status: { name: uniqueItemStatus[0] ? statusItems[uniqueItemStatus[0]]?.label : '' },
      }"
      :newStatusTransition="{ status: { key: newStatus, name: statusItems[newStatus]?.label } }"
      :hideStatusPreview="uniqueItemStatus.length > 1"
      @transition="handleBulkTransition"
    >
      <template #title>
        <span class="transition-modal-title">{{ $t('salesOrder.modal.transition.title') }}</span>
      </template>
      <template #confirmationText>
        <p
          v-html="
            $t('salesOrder.modal.transition.content', {
              newStatus: statusItems[newStatus]?.label,
              total: selectedItems.length,
            })
          "
        />
      </template>
    </DefaultTransitionModal>

    <!-- Transition to CANCELED -->
    <TransitionToCancelModal
      ref="modal-canceled"
      :entityData="{
        status: { name: uniqueItemStatus[0] ? statusItems[uniqueItemStatus[0]] : '' },
      }"
      :newStatusTransition="{ status: { key: status.CANCELED, name: statusItems[status.CANCELED]?.label } }"
      :selectedItems="selectedItems"
      @transition="handleBulkTransition"
    />

    <!-- Loading Bulk Action -->
  </Layout>
</template>

<script>
import appConfig from '@/app.config'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McTable from '@/components/McTable'
import McInlineFilter from '@/components/McInlineFilter'
import McTableFilter from '@/components/McTableFilter'
import McFilterSelect from '@/components/McFilterSelect'
import DefaultTransitionModal from '@/components/McTransitionStatus/DefaultTransitionModal'

import InputTableFilter from './components/InputTableFilter'
import ActionDropdown from './components/ActionDropdown'
import TransitionToCancelModal from './components/TransitionToCancelModal'

import * as SalesOrder from '@/services/rest-api/lib/sales-order/sales-order'

import fields from './lib/fields/listTableFields'
import inlineFilterItems from './lib/constants/inline-filter-items'
import filterMappings from './lib/constants/filter-mappings'
import formatFilterMappings from './lib/constants/format-filter-mappings'
import parseFilterDataMappings from './lib/constants/parse-filter-data-mappings'
import { successMsg, errorMsg } from './lib/constants/toast-msg'
import statuses, { successToastMsg, failedToastMsg, status } from './lib/constants/status'

import { formatIndexDate } from '@/helpers/tzDate'
import { isEqual } from 'lodash'
import { mapGetters } from 'vuex'

export default {
  page: {
      title: 'Sales Order',
      meta: [{ name: 'description', content: appConfig.description }],
    },
  components: {
    Layout,
    PageHeader,
    McButton,
    McTable,
    McInlineFilter,
    McTableFilter,
    InputTableFilter,
    McFilterSelect,
    ActionDropdown,
    DefaultTransitionModal,
    TransitionToCancelModal,
  },
  mixins: [fields],
  data() {
    return {
      status,

      activeFilters: {},
      initialParams: {},
      selectedItems: [],
      tableKey: 0,
      newStatus: null,
      isTableDataAvailable: true,
      isTransitionLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
    }),
    title() {
      return 'Sales Order'
    },
    subTitle() {
      return this.$t('salesOrder.subTitle.list')
    },
    inlineFilterItems() {
      return inlineFilterItems(this)
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    apiClient() {
      let getSalesOrder = SalesOrder.get(null, this.handleSetParams, null)

      if (Object.keys(this.activeFilters).length > 0) {
        const queryParamFilters = []

        Object.keys(this.activeFilters).forEach((key) => {
          const filter = filterMappings[key]?.(key, this.activeFilters[key]) ?? undefined
          queryParamFilters.push(filter)
        })

        getSalesOrder = SalesOrder.get(queryParamFilters, this.handleSetParams, null)
      }

      return {
        get: getSalesOrder,
        delete: SalesOrder.bulkDelete,
      }
    },
    computedStatus() {
      return (value) => {
        if (this.statusItems[value]) {
          return this.statusItems[value]?.label
        }

        return '-'
      }
    },
    statusItems() {
      return statuses(this)
    },
    uniqueItemStatus() {
      const uniqueStatus = []
      for (const item of this.selectedItems) {
        const status = item?.status?.raw_type
        if (status === undefined) continue

        if (!uniqueStatus.includes(status)) {
          uniqueStatus.push(status)
        }
      }

      return uniqueStatus
    },
    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-data.svg`)
      }
      return require(`@/assets/images/illustration/saas/no-data.svg`)
    },
  },
  created() {
    this.setFilterFromURL()
  },
  methods: {
    formatIndexDate,

    handleDataAvailable(isAvailable) {
      if (Object.keys(this.activeFilters).length > 0) {
        this.isTableDataAvailable = true
        return
      }

      this.isTableDataAvailable = isAvailable
    },

    handleSetParams(params) {
      this.setURLParams(params)
    },

    // change current URL based on params changes
    setURLParams(params) {
      const newParams = {}

      // loop the params to set the newParams
      for (const [key, val] of Object.entries(params)) {
        // skip the undefined value
        if (val == undefined) continue

        // immediately set the value if it's not an Array
        if (!Array.isArray(val)) {
          newParams[key] = typeof val === 'string' ? val : val.toString()

          continue
        }

        // value with Array type usually is filters/sorts, so we need
        // to iterate it and set to each key
        val.forEach((item, index) => {
          newParams[key + index] = item
        })
      }

      // check if the URL has same values with new params
      const isSameParams = isEqual(newParams, this.$route.query)
      if (isSameParams) return

      // redirect to new query params
      const urlPath = '/sales-order?' + new URLSearchParams(newParams).toString()
      this.$router.replace(urlPath)
    },

    // change filters from current URL
    setFilterFromURL() {
      const activeFilters = new Map()
      for (const [key, val] of Object.entries(this.$route.query)) {
        // set initial params exclude filters (page, limit, search)
        if (!key.includes('filters')) {
          let value
          if (key === 'search') {
            value = val || '-'
          } else if (key === 'limit') {
            value = ['10', '25', '50', '100'].includes(val) ? Number(val) || 1 : 10
          } else if (key === 'page') {
            value = Number(val) || 1
          }

          this.$set(this.initialParams, key, value)
          continue
        }

        const field = val.split(' ')[0]

        // TODO: Set filters from table filter
        if (field !== 'status.name') continue

        const { op, value } = formatFilterMappings[field](val)
        let newKey = `${field}:${op}`

        if (field === 'status.name') {
          newKey = field
        }

        // set new key in active filters
        activeFilters.set(newKey, value)
      }

      // if there are active filters, set the new values
      if (activeFilters.size == 0) return

      this.activeFilters = Object.fromEntries(activeFilters)
    },
    handleChangeActiveFilters(filters) {
      /*
        filters is Array of Object
        example:
        [
          {
            key: 'created_on:lte',
            values: '2023-08-29T17:00:00Z'
          }
        ]
      */

      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null or undefined
        if (filter.values === null || filter.values === undefined || filter.values === '') return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },

    handleAdd() {
      this.$router.push({ name: 'Sales Order Create' })
    },

    handleClickCheckbox(ids) {
      if (Object.keys(ids).length === 0) {
        this.selectedItems = []
        return
      }

      const items = this.$refs['table-sales-order'].items
      for (const [key, val] of Object.entries(ids)) {
        const item = items.find((v) => v.id === key)
        const index = this.selectedItems.findIndex((v) => v?.id === key)

        if (!val) {
          if (index === -1) continue

          this.selectedItems.splice(index, 1)
          continue
        }

        if (index !== -1) continue

        let newItem = item

        if (item === undefined) {
          newItem = {
            id: key,
          }
        }

        this.selectedItems.push(newItem)
      }
    },

    resetSelectedItems() {
      this.selectedItems = []
      this.$refs['table-sales-order'].ResetSelects()
    },

    handleClickRow(item) {
      const query = this.$route.query

      this.$router.push({ name: 'Sales Order Detail', params: { id: item.id }, query })
    },

    handleOpenNewTab(item) {
      const query = this.$route.query

      return this.$router.resolve({
        name: 'Sales Order Detail',
        params: { id: item.id },
        query,
      }).href
    },

    handleTransitionTo(newStatus) {
      this.newStatus = newStatus
      this.$refs['modal-transition'].show()
    },

    handleTransitionToCanceled() {
      this.$refs['modal-canceled'].show()
    },

    async handleBulkTransition(data) {
      this.isTransitionLoading = true

      this.$refs['modal-transition'].hide()
      this.$refs['modal-canceled'].hide()
      
      const statusId = data.transition.status.key
      
      const ids = []
      this.selectedItems.forEach((item) => {
        if (!item) return

        ids.push(item.id)
      })

      const res = await SalesOrder.bulkTransition({
        ids,
        status: statusId,
        fields: data.additionalPayload?.fields,
      })

      if (res.errors) {
        this.isTransitionLoading = false
        this.resetSelectedItems()
        this.$mcToast.error(errorMsg['transition/default'](this))

        return
      }

      if (res.data.success_count > 0) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: `<strong>${res.data.success_count}</strong> ${successToastMsg[statusId](
                  this
                )}`,
              },
            ],
          },
        })
      }

      if (res.data.failed_count > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: `<strong>${res.data.failed_count}</strong> ${failedToastMsg[statusId](this)}`,
              },
            ],
          },
        })
      }

      this.tableKey += 1
      this.resetSelectedItems()
      this.isTransitionLoading = false
    },

    handleDeleteSuccess({ data }) {
      if (data.success_count > 0) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: successMsg['delete-so/list'](data.success_count),
              },
            ],
          },
        })
      }

      if (data.failed_count > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: errorMsg['delete-so/list'](data.failed_count),
              },
            ],
          },
        })
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.table-sales-order {
  :deep(.__status-dots) {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }
}
</style>
